import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DrawerLink } from '../../app/DrawerLink';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { AlbumEditor } from './AlbumEditor';
import { albums } from './api';

/**
 * The albums tab on a profile.
 *
 * The owner also sees their unlisted and private ones, badged - the same rule
 * the track list uses, for the same reason: an album you cannot see on your own
 * profile is an album you will assume failed to save.
 */
export function AlbumList({
  username,
  isOwner,
  compact = false,
}: {
  username: string;
  isOwner: boolean;
  /**
   * Rendered in a half-width column rather than across the page.
   *
   * Still three covers a row, but it takes a smaller breakpoint to get there -
   * the grid is sized to the viewport, not to its container, so a column that
   * is half the page reaches three-across at about twice the window width the
   * full-width grid needs.
   */
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['albums', username],
    queryFn: () => albums.list(username),
  });

  if (isPending) {
    return (
      <div className="mt-4 flex flex-col gap-2" aria-busy="true">
        {[0, 1].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="mt-4">
      {isOwner && (
        <div className="mb-4">
          <Button variant="secondary" onClick={() => setCreating(true)}>
            {copy['cta.newAlbum']}
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-soft">
            {isOwner
              ? 'No albums yet. Group your tracks into a release to give them somewhere to live.'
              : `@${username} hasn't published any albums yet.`}
          </p>
        </Card>
      ) : (
        <ul
          className={`grid gap-4 sm:grid-cols-2 ${compact ? 'xl:grid-cols-3' : 'lg:grid-cols-3'}`}
          data-album-list
        >
          {items.map((album) => (
            <li key={album.id} data-album-id={album.id}>
              <DrawerLink to={`/albums/${album.id}`} className="group block">
                <Card className="overflow-hidden transition-colors group-hover:border-line-lit">
                  <div className="aspect-square w-full overflow-hidden bg-raised">
                    {album.artwork_small ? (
                      <img
                        src={album.artwork_small}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      // A patterned placeholder rather than an empty box, so a
                      // coverless album still reads as an album in the grid.
                      <div
                        className="h-full w-full"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(45deg, var(--color-blank) 0 10px, var(--color-blank-alt) 10px 20px)',
                        }}
                      />
                    )}
                  </div>

                  <div className="p-3">
                    <p className="truncate text-sm font-medium text-ink">{album.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="meta-caps">
                        {album.track_count}{' '}
                        {album.track_count === 1 ? copy['noun.track'] : copy['noun.track.plural']}
                      </span>
                      {album.visibility !== 'public' && (
                        <Chip>{album.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                      )}
                    </div>
                  </div>
                </Card>
              </DrawerLink>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <AlbumEditor
          onClose={() => setCreating(false)}
          onSaved={(album) => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['albums', username] });
            // Straight into the new album, because the next thing anybody wants
            // after creating one is to put tracks on it.
            navigate(`/albums/${album.id}`);
          }}
        />
      )}
    </div>
  );
}
